import { useEffect, useState } from "react";
import axios from "axios";
import { Search, X } from "lucide-react";
import { CATEGORIES, EXERCISE_TYPE_LABELS } from "@/lib/workoutTaxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExercisePickerDialog({ category, open, onClose, onPick }) {
    const [list, setList] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState(category || "");

    useEffect(() => {
        if (!open) return;
        setCat(category || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, category]);

    useEffect(() => {
        if (!open) return;
        const run = async () => {
            const params = {};
            if (cat) params.category = cat;
            if (q.trim()) params.q = q.trim();
            const r = await axios.get(`${API}/coach/exercises`, { params, withCredentials: true });
            setList(r.data || []);
        };
        run();
    }, [open, cat, q]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end md:items-center justify-center p-0 md:p-6">
            <div data-testid="exercise-picker" className="bg-zinc-950 border border-zinc-800 w-full md:max-w-2xl max-h-[85vh] flex flex-col">
                <div className="h-1 bg-brand-red" />
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
                    <div className="font-display text-2xl tracking-wider text-white uppercase">Pick an exercise</div>
                    <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4 border-b border-zinc-900 space-y-3">
                    <div className="relative">
                        <Search className="w-4 h-4 text-zinc-300 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search…"
                            data-testid="exercise-picker-search"
                            className="w-full bg-black border border-zinc-800 pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand-red"
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <button type="button" onClick={() => setCat("")} className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 ${cat === "" ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-200"}`}>All</button>
                        {CATEGORIES.map((c) => (
                            <button key={c.value} type="button" onClick={() => setCat(c.value)} className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 ${cat === c.value ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-200"}`}>{c.label}</button>
                        ))}
                    </div>
                </div>
                <div className="overflow-y-auto flex-1">
                    {list.length === 0 ? (
                        <div className="p-10 text-center text-sm text-zinc-300">No exercises found.</div>
                    ) : (
                        list.map((ex) => (
                            <button
                                key={ex.exercise_id}
                                type="button"
                                data-testid={`exercise-pick-${ex.exercise_id}`}
                                onClick={() => { onPick(ex); onClose(); }}
                                className="w-full text-left px-5 py-3 border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/60 transition-colors"
                            >
                                <div className="text-sm font-semibold text-white">{ex.exercise_name}</div>
                                <div className="text-[11px] text-zinc-300">
                                    {ex.category} · {EXERCISE_TYPE_LABELS[ex.exercise_type] || ex.exercise_type}
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
